/* eslint-disable @typescript-eslint/naming-convention */
import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import Lottie from 'lottie-react';
import Modal from '@/lib/Modal';
import LoadingRing from '@/animations/loading-ring.json';

import { fetchEmiPlans } from '@/api/plans';
import { useGlobalStore } from '@/store';
import EmiPlansFailure from '@/shared/Components/EmiPlansFailure';
import CollectPanModal from '@/shared/Components/CollectPanModal';
import { formRequest } from '@/utils/helpers';
import { updatePayment } from '@/api/payment';
import { getUserLoggedInStatus, trackHelper } from '@/analytics/utils';
import { ANALYTICS_ACTIONS, EVENT_NAMES } from '@/analytics/constants';

type EmiPlansLoaderType = {
  isLoading: boolean;
  onDismiss: () => void;
};

const EmiPlansLoader = ({ isLoading, onDismiss }: EmiPlansLoaderType): JSX.Element => {
  const setView = useGlobalStore((state) => state.setActiveView);
  const { user, setLenderDetails, activeView, userAccessToken } = useGlobalStore();
  const setEmiPlans = useGlobalStore((state) => state.setEmiPlans);
  const [isPopupActive, setIsActive] = useState(isLoading);
  const [showFailurePopup, setShowFailurePopup] = useState(false);
  const [showEditPan, setShowEditPan] = useState(false);

  const handleFetchEmiPlans = async (): Promise<void> => {
    try {
      const res = await fetchEmiPlans(user.pin_code ?? '');
      // Track plans fetch Response
      trackHelper({
        actionName: ANALYTICS_ACTIONS.BACKEND_RESPONSE,
        objectName: EVENT_NAMES.OFFERS_FETCH_RESP,
        screen: activeView,
        properties: {
          contact: user.contact,
          user_type: getUserLoggedInStatus(userAccessToken, user.contact),
          offer_available: res.plans?.length ? 'true' : 'false',
        },
      });
      if (res.plans?.length) {
        setView('EMI_PLANS');
        setEmiPlans(res.plans);
        if (res.lender_details) {
          setLenderDetails(res.lender_details);
        }
        return;
      }

      if (!res.plans?.length) {
        setShowFailurePopup(true);
        setIsActive(false);
        // Track plans fetch Response
        trackHelper({
          actionName: ANALYTICS_ACTIONS.BACKEND_RESPONSE,
          objectName: EVENT_NAMES.OFFERS_FETCH_RESP,
          screen: activeView,
          properties: {
            contact: user.contact,
            user_type: getUserLoggedInStatus(userAccessToken, user.contact),
            offer_available: res.plans?.length ? 'true' : 'false',
          },
        });
      }
    } catch (err) {
      setShowFailurePopup(true);
      setIsActive(false);
      console.error(err);
    }
  };

  const handleCancel = async (): Promise<void> => {
    try {
      const res = await updatePayment({
        status: 'failed',
        // eslint-disable-next-line @typescript-eslint/naming-convention
        failure_reason: 'cancelled_by_user',
      });
      if (res.data.gateway_payload) {
        const { url, payload, method } = res.data.gateway_payload;
        formRequest(url, payload, method);
      }
    } catch (e: unknown) {
      //
    }
  };

  const handlePlanFailure = (selected: string): void => {
    if (selected === 'cancel') {
      handleCancel();
      return;
    }

    if (selected === 'edit_pan') {
      setShowFailurePopup(false);
      setShowEditPan(true);
    }
  };

  useEffect(() => {
    handleFetchEmiPlans();
    // Track fetch emi plans loader render
    trackHelper({
      actionName: ANALYTICS_ACTIONS.RENDER,
      objectName: EVENT_NAMES.EMI_PLANS_LOADER_RENDER,
      screen: activeView,
      properties: {
        contact: user.contact,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        user_type: getUserLoggedInStatus(userAccessToken, user.contact),
      },
    });
  }, []);

  return (
    <>
      <Modal isOpen={isPopupActive} onDismiss={onDismiss}>
        <Modal.Body>
          <div data-testid="explore-emi-plans" className="flex flex-col items-center">
            <Image width={115} height={19} alt="search" src="/search.svg" />
            <p className="my-5 text-on-grey-subtle text-center mb-0">
              Get ready to shop as we explore the best EMI offers for you..
            </p>
            <div className="w-36 min-h-10">
              <Lottie
                className="top-0 h-full scale-90 m-auto left-0 right-0}"
                loop={true}
                animationData={LoadingRing}
              />
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {showFailurePopup ? (
        <EmiPlansFailure
          isOpen={showFailurePopup}
          onDismiss={(): void => {
            setShowFailurePopup(false);
            onDismiss();
          }}
          onClick={handlePlanFailure}
        />
      ) : null}

      {showEditPan ? (
        <CollectPanModal
          onSuccess={(): void => {
            setShowEditPan(false);
            setView('DETAILS');
          }}
          isOpen={showEditPan}
          onDismiss={(): void => {
            setShowEditPan(false);
          }}
        />
      ) : null}
    </>
  );
};

export default EmiPlansLoader;
